import { DestroyRef, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, of, race, Subject, throwError, timer } from 'rxjs';
import { catchError, filter, first, map, switchMap } from 'rxjs/operators';

import {
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { PrlwAuthError } from '../errors/error-types/error-types';
import { AuthErrorCode } from '../errors/error-codes/client-error-codes';
import { LogoutController } from '@prlw/core/auth/logout/logout.controller';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const AUTH_HEADER_NAME = 'Authorization';
const AUTH_HEADER_PREFIX = 'Bearer ';
const NEED_REFRESH_HTTP_STATUS = 401;

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly logoutController: LogoutController,
    private readonly publicEventsService: PublicEventsService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.publicEventsService
      .registerForEvents()
      .pipe(
        filter((event) => event.type === EventTypes.NewAuthenticationResult),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((value) => {
        this.newAuthenticationResult$.next(value);
      });
  }

  newAuthenticationResult$ = new Subject<unknown>();

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (request.headers.get('SkipAuth')) {
      const cleanedRequest = request.clone({
        headers: request.headers.delete('SkipAuth'),
      });
      return next.handle(cleanedRequest);
    }
    return this.withToken(request).pipe(
      switchMap((requestWithToken) => next.handle(requestWithToken)),
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === NEED_REFRESH_HTTP_STATUS
        ) {
          return race(timer(6 * 1000), this.newAuthenticationResult$).pipe(
            switchMap(() => this.retry(request, next)),
          );
        }
        return throwError(() => error);
      }),
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.error.message === AuthErrorCode.MultipleLogin
        ) {
          this.logoutController.logoutWithError(
            new PrlwAuthError(AuthErrorCode.MultipleLogin),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private retry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.withToken(request).pipe(
      switchMap((requestWithToken) => next.handle(requestWithToken)),
    );
  }

  private withToken(
    request: HttpRequest<unknown>,
  ): Observable<HttpRequest<unknown>> {
    return request.url.includes(`${process.env['OIDC_PROVIDER_URL']}/api/oidc`)
      ? of(request)
      : this.oidcSecurityService.getIdToken().pipe(
          first(),
          map((idToken) =>
            request.clone({
              setHeaders: {
                [AUTH_HEADER_NAME]: `${AUTH_HEADER_PREFIX}${idToken}`,
              },
            }),
          ),
        );
  }
}
